import { ItemImage } from '@components/CatalogElements';
import { CommonUtility } from '@utility/common';
import {
  BoldText,
  DangerText,
  MuteText,
  InfoTooltipPopup,
  CenterFlexRow,
} from '@elements/Common';
import {
  ActionHoverTableRow,
  BorderTableCell,
  AppTableCell,
} from '@components/AppTable';
import { Fragment, useMemo, useState } from 'react';
import { ProductProperties } from '@page-components/scm/common';
import TextCopyToClipboard from '@components/TextCopyToClipboard';
import { Button, cn } from '@skupreme/shared/ui';
import { DarkPopup } from '@elements/Popup';
import {
  CalendarIcon,
  CurrencyDollarIcon,
  PackageIcon,
  PencilSimpleIcon,
  TrashIcon,
  TruckIcon,
} from '@phosphor-icons/react';
import { validateAttribute } from '@page-components/shipment';
import { CatalogHelpText } from '@utility/constants/strings';
import { ItemOtherSuppliers } from '@components/ItemOtherSuppliers';
import { ImageWithActionCell } from '@elements/Table';
import { DeleteConfirmWithButton } from '@components/DeleteConfirm';
import { ItemWiseAllocation } from './Allocation';
import { GroupedMOQPopup } from './GroupedMOQPopup';
import { type IUPCQty, validatedMOQ } from './common';
import {
  AddAliasButton,
  ALIAS_ACTION,
  AliasActions,
  AliasTableRow,
  CartonQtyCell,
  CartonQtyInput,
  ExpandToggle,
  ItemPrice,
  QtyErrorMessage,
  RowLine,
  SmallMuteText,
  TextAction,
} from './row-parts';
import type { IPOProduct, IPOAttribute } from '@hooks/purchase-order-items';
import type { ITimelineItem } from '../../pages/app/scm/purchase-orders/create';

interface IUPCQtyItem {
  [key: string]: IUPCQty;
}

interface IAllocationItem {
  key: string;
  [key: string]: unknown;
}

interface IAllocationData {
  allocations: IAllocationItem[];
  [key: string]: unknown;
}

interface IItemWiseAllocation {
  [key: string]: IAllocationData;
}

interface IMultiAliasItemRowProps {
  item?: IPOProduct;
  upcQty: IUPCQtyItem;
  currency?: string;
  qtyChanged: (
    item: IPOProduct,
    alias: string,
    field: string,
    value: number,
  ) => void;
  addEditAttribute: (item: IPOProduct, alias?: string) => void;
  showTimeline?: boolean;
  setCurrentItem: (item: ITimelineItem) => void;
  setRegionId: (regionId: string | null) => void;
  canTakeAction: boolean;
  isPublic?: boolean;
  showPrediction?: boolean;
  readOnly?: boolean;
  editLeadTimeClicked?: (item: IPOProduct) => void;
  editPriceClicked: (item: IPOProduct) => void;
  markUnitAvailableToShipClicked?: (item: IPOProduct) => void;
  itemWiseAllocations?: IItemWiseAllocation;
  itemSupplierClick?: (item: IPOProduct) => void;
  deleteAttribute?: (item: IPOProduct, alias: string) => void;
  removeItem?: (item: IPOProduct) => void;
  imperial?: boolean;
}

export function MultiAliasItemRow({
  item,
  upcQty,
  currency,
  qtyChanged,
  addEditAttribute,
  showTimeline,
  setCurrentItem,
  setRegionId,
  canTakeAction,
  isPublic,
  showPrediction = true,
  readOnly,
  editLeadTimeClicked,
  editPriceClicked,
  markUnitAvailableToShipClicked,
  itemWiseAllocations,
  itemSupplierClick,
  deleteAttribute,
  removeItem,
  imperial = false,
}: IMultiAliasItemRowProps) {
  if (!item) {
    return null;
  }

  const [hover, setHover] = useState(false);
  const [expand, setExpand] = useState(false);
  const validAttributes = useMemo(
    () => (item?.attributes || []).filter(x => !!x.alias),
    [item?.attributes],
  );
  const latestAttribute = useMemo(
    () =>
      (validAttributes || []).length === 1
        ? { ...validAttributes[0] }
        : (null as IPOAttribute | null),
    [validAttributes],
  );
  const region_id = item?.regional_items
    ? item.regional_items.find(x => x.master)?.marketplace_region_id ||
      item.regional_items[0]?.marketplace_region_id
    : null;

  const newMOQError = useMemo(() => {
    if (item?.upc && upcQty[item.upc]?.qty && latestAttribute) {
      return validatedMOQ(item, upcQty, latestAttribute);
    }
    return null;
  }, [upcQty, item, latestAttribute]);

  const isValidVariant = useMemo(
    () => validateAttribute(latestAttribute),
    [latestAttribute],
  );

  const menuActions = useMemo(() => {
    if (!canTakeAction) {
      return [];
    }
    const temp = [
      {
        title: 'Edit Item Cost',
        icon: <CurrencyDollarIcon />,
        onClick: () => editPriceClicked(item),
      },
    ];
    if (editLeadTimeClicked) {
      temp.push({
        title: 'Edit Lead Time',
        icon: <CalendarIcon />,
        onClick: () => editLeadTimeClicked(item),
      });
    }
    temp.push({
      title: 'Create an Alias',
      icon: <PackageIcon />,
      onClick: () => addEditAttribute(item),
    });
    if ((validAttributes || []).length === 1 && latestAttribute) {
      temp.push({
        title: 'Edit Current Alias',
        icon: <PencilSimpleIcon />,
        onClick: () => addEditAttribute(item, latestAttribute.alias),
      });
    }
    if (
      item?.upc &&
      (item?.shippable_qty || 0) < (upcQty[item.upc]?.qty || 0) &&
      markUnitAvailableToShipClicked
    ) {
      temp.push({
        title: 'Mark Units Available to Ship',
        icon: <TruckIcon />,
        onClick: () => markUnitAvailableToShipClicked(item),
      });
    }
    if (removeItem) {
      temp.push({
        title: 'Remove Item',
        icon: <TrashIcon />,
        onClick: () => removeItem(item),
      });
    }
    return temp;
  }, [
    item,
    canTakeAction,
    editLeadTimeClicked,
    removeItem,
    validAttributes,
    latestAttribute,
    upcQty,
    markUnitAvailableToShipClicked,
  ]);

  return (
    <Fragment key={item.upc}>
      <ActionHoverTableRow
        className={hover ? 'hover' : ''}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        textAlign="center"
        verticalAlign="middle"
      >
        <BorderTableCell expand={expand ? 'true' : 'false'}>
          <div className="border" />
          <ImageWithActionCell
            image={<ItemImage item={item} />}
            menus={menuActions}
            className={undefined}
          />
        </BorderTableCell>
        <AppTableCell textAlign="left">
          <RowLine>{item.g_attributes?.short_title || item.p_title}</RowLine>
          <RowLine className="sk:justify-start">
            <MuteText className="sk:mr-2">SKU</MuteText>
            <BoldText>
              <TextCopyToClipboard text={item.p_sku} iconSize={14} />
            </BoldText>
          </RowLine>
          {item.unique_identifier_type && (
            <RowLine className="sk:justify-start">
              <MuteText className="sk:mr-2">
                {item.unique_identifier_type || 'UPC'}
              </MuteText>
              <BoldText>
                <TextCopyToClipboard
                  text={item.secondary_identifier || item.upc}
                  iconSize={14}
                />
              </BoldText>
            </RowLine>
          )}
          <ItemOtherSuppliers
            onClick={itemSupplierClick}
            list={item.assignedSuppliers}
          />
          {itemWiseAllocations && itemWiseAllocations[item.upc] && (
            <ItemWiseAllocation
              allocationData={itemWiseAllocations[item.upc]}
            />
          )}
        </AppTableCell>
        <AppTableCell textAlign="left">
          {(validAttributes || []).length > 1 ? (
            <RowLine>
              <div>
                <MuteText>Multiple Aliases</MuteText>
                <ExpandToggle
                  expanded={expand}
                  onToggle={() => setExpand(!expand)}
                />
              </div>
              {!readOnly && (
                <AddAliasButton onPress={() => addEditAttribute(item)} />
              )}
            </RowLine>
          ) : latestAttribute ? (
            <ProductProperties
              unitText={item.unit_of_measurement}
              attributes={latestAttribute}
              imperial={imperial}
              isMultiple={false}
            />
          ) : null}
        </AppTableCell>
        <CartonQtyCell width={2}>
          {(validAttributes || []).length ? (
            <>
              {validAttributes.length === 1 && latestAttribute && (
                <>
                  {!isValidVariant ? (
                    latestAttribute.alias ? (
                      <TextAction
                        onClick={() =>
                          addEditAttribute(item, latestAttribute.alias)
                        }
                      >
                        <DangerText>Invalid Carton Configuration</DangerText>
                      </TextAction>
                    ) : (
                      <TextAction onClick={() => addEditAttribute(item)}>
                        <DangerText>Add New Carton Configuration</DangerText>
                      </TextAction>
                    )
                  ) : (
                    <>
                      {readOnly ? (
                        CommonUtility.numberWithCommas(
                          upcQty[item.upc]?.aliases[latestAttribute.alias]
                            ?.ctn_qty,
                        )
                      ) : (
                        <CartonQtyInput
                          value={
                            upcQty[item.upc]?.aliases && latestAttribute.alias
                              ? (upcQty[item.upc]?.aliases[
                                  latestAttribute.alias
                                ]?.ctn_qty ?? null)
                              : null
                          }
                          onChange={value =>
                            qtyChanged(
                              item,
                              latestAttribute.alias,
                              'ctn_qty',
                              typeof value === 'string'
                                ? parseFloat(value) || 0
                                : value,
                            )
                          }
                          isInvalid={
                            !!newMOQError || !(item.prices || []).length
                          }
                          maxValue={
                            200000000 / (latestAttribute.units_per_carton || 1)
                          }
                        />
                      )}
                    </>
                  )}
                </>
              )}
              {(item.moq_grouping?.items || []).length > 0 ? (
                <CenterFlexRow
                  className={
                    validAttributes.length === 1 ? 'sk:my-2' : undefined
                  }
                >
                  <SmallMuteText>MOQ</SmallMuteText>
                  <GroupedMOQPopup data={item.moq_grouping} />
                </CenterFlexRow>
              ) : (
                <CenterFlexRow
                  className={
                    validAttributes.length === 1 ? 'sk:my-2' : undefined
                  }
                >
                  <SmallMuteText>
                    MOQ: {CommonUtility.numberWithCommas(item.MOQ || 0)}
                  </SmallMuteText>
                </CenterFlexRow>
              )}
              {newMOQError && <QtyErrorMessage>{newMOQError}</QtyErrorMessage>}
            </>
          ) : (
            <TextAction onClick={() => addEditAttribute(item)}>
              <DangerText>Add New Carton Configuration</DangerText>
            </TextAction>
          )}
        </CartonQtyCell>
        {showPrediction && (
          <AppTableCell width={2}>
            {showTimeline ? (
              <StatusRenderer
                onClick={() => {
                  setCurrentItem({
                    ...item,
                    units_per_carton: latestAttribute?.units_per_carton || 0,
                  });
                  setRegionId(region_id);
                }}
                item={item}
              />
            ) : item.predictedQty !== undefined &&
              item.predictedQty !== null ? (
              <DarkPopup
                trigger={<StatusRenderer item={item} onClick={undefined} />}
              >
                Click on the prediction quantity to update your order and
                optimize your supply chain
              </DarkPopup>
            ) : (
              <StatusRenderer item={item} onClick={undefined} />
            )}
          </AppTableCell>
        )}
        <AppTableCell>
          {CommonUtility.numberWithCommas(upcQty[item.upc]?.qty)}
        </AppTableCell>
        <AppTableCell>
          {(item.prices || []).length === 0 ? (
            <TextAction
              onClick={() => editPriceClicked(item)}
              title="Supplier Cost is not available, Click here to set it"
              className="sk:font-bold"
            >
              <DangerText>Set Cost</DangerText>
            </TextAction>
          ) : (
            <>
              {ItemPrice(
                readOnly,
                item.prices,
                upcQty[item.upc]?.current_price,
                currency,
              )}
            </>
          )}
        </AppTableCell>
        <AppTableCell className="action-cell sk:whitespace-nowrap">
          {CommonUtility.currencyFormat(
            upcQty[item.upc]?.total,
            currency,
            undefined,
            4,
          )}
        </AppTableCell>
      </ActionHoverTableRow>
      {(validAttributes || []).length > 1 && expand && (
        <ChildItemList
          data={validAttributes}
          currentItem={item}
          upcQty={upcQty}
          qtyChanged={qtyChanged}
          editAttribute={addEditAttribute}
          showPrediction={showPrediction}
          readOnly={readOnly}
          isPublic={isPublic}
          deleteAttribute={deleteAttribute}
          hasError={newMOQError}
          currency={currency}
        />
      )}
    </Fragment>
  );
}

interface IAliasItemRowProps {
  item: IPOProduct;
  upcQty: IUPCQtyItem;
  qtyChanged: (
    item: IPOProduct,
    alias: string,
    field: string,
    value: number,
  ) => void;
  attribute: IPOAttribute;
  editAttribute: (item: IPOProduct, alias?: string) => void;
  index: number;
  showPrediction?: boolean;
  readOnly?: boolean;
  isPublic?: boolean;
  deleteAttribute?: (item: IPOProduct, alias: string) => void;
  hasError: string | null;
  currency?: string;
}

function AliasItemRow({
  item,
  upcQty,
  qtyChanged,
  attribute,
  editAttribute,
  index,
  showPrediction,
  readOnly,
  isPublic,
  deleteAttribute,
  hasError,
  currency,
}: IAliasItemRowProps) {
  const currentAlias = useMemo(
    () => upcQty[item.upc]?.aliases[attribute.alias],
    [upcQty, item, attribute],
  );

  const isValidVariant = useMemo(
    () => validateAttribute(attribute),
    [attribute],
  );

  return (
    <AliasTableRow
      textAlign="center"
      verticalAlign="middle"
      key={attribute.alias}
    >
      <BorderTableCell expand="true" colSpan="2" className="sk:pl-12!">
        <div className="border" />
        <RowLine>
          <SmallMuteText className="sk:flex sk:items-center">
            {attribute.primary ? 'Primary Carton Alias' : 'Carton Alias'}
            <InfoTooltipPopup
              position={isPublic ? 'top right' : undefined}
              title={CatalogHelpText.carton}
            />
          </SmallMuteText>
        </RowLine>
        <RowLine>
          <BoldText>
            <TextCopyToClipboard text={attribute.alias} iconSize={14} />
          </BoldText>
        </RowLine>
        {!readOnly && (
          <AliasActions>
            <Button
              variant="primary"
              shape="circle"
              aria-label="Edit alias"
              isDisabled={!!readOnly}
              className={ALIAS_ACTION}
              onPress={() => editAttribute(item, attribute.alias)}
            >
              <PencilSimpleIcon size={16} aria-hidden />
            </Button>
            {deleteAttribute && (
              <DeleteConfirmWithButton
                disabled={!!readOnly}
                buttonClass={cn('sk:p-2!', ALIAS_ACTION)}
                size="mini"
                onConfirm={() => deleteAttribute(item, attribute.alias)}
                buttonText={<TrashIcon size={16} aria-label="Delete alias" />}
              />
            )}
          </AliasActions>
        )}
      </BorderTableCell>
      <AppTableCell>
        <ProductProperties
          unitText={item.unit_of_measurement}
          attributes={attribute}
          showAlias={false}
          isMultiple={false}
        />
      </AppTableCell>
      <CartonQtyCell width={2}>
        {readOnly ? (
          CommonUtility.numberWithCommas(currentAlias?.ctn_qty)
        ) : (
          <>
            {isValidVariant ? (
              <CartonQtyInput
                value={currentAlias?.ctn_qty ?? null}
                onChange={value =>
                  qtyChanged(
                    item,
                    attribute.alias,
                    'ctn_qty',
                    typeof value === 'string' ? parseFloat(value) || 0 : value,
                  )
                }
                isInvalid={!!hasError || !(item.prices || []).length}
                maxValue={200000000 / (attribute?.units_per_carton || 1)}
              />
            ) : (
              <TextAction onClick={() => editAttribute(item, attribute.alias)}>
                <DangerText>Invalid Carton Configuration</DangerText>
              </TextAction>
            )}
          </>
        )}
      </CartonQtyCell>
      {showPrediction && <AppTableCell />}
      <AppTableCell>
        {CommonUtility.numberWithCommas(currentAlias?.qty || '')}
      </AppTableCell>
      <AppTableCell />
      <AppTableCell className="action-cell sk:whitespace-nowrap">
        {CommonUtility.currencyFormat(
          (currentAlias?.qty || 0) * (upcQty[item.upc]?.current_price || 0),
          currency,
          undefined,
          4,
        )}
      </AppTableCell>
    </AliasTableRow>
  );
}

interface IChildItemListProps {
  data: IPOAttribute[];
  upcQty: IUPCQtyItem;
  qtyChanged: (
    item: IPOProduct,
    alias: string,
    field: string,
    value: number,
  ) => void;
  currentItem: IPOProduct;
  editAttribute: (item: IPOProduct, alias?: string) => void;
  deleteAttribute?: (item: IPOProduct, alias: string) => void;
  showPrediction?: boolean;
  readOnly?: boolean;
  isPublic?: boolean;
  hasError: string | null;
  currency?: string;
}

function ChildItemList({
  data,
  upcQty,
  qtyChanged,
  currentItem,
  editAttribute,
  deleteAttribute,
  showPrediction,
  readOnly,
  isPublic,
  hasError,
  currency,
}: IChildItemListProps) {
  return (
    <>
      {(data || []).map((item, index) => (
        <AliasItemRow
          key={item.alias}
          currency={currency}
          item={currentItem}
          attribute={item}
          upcQty={upcQty}
          qtyChanged={qtyChanged}
          editAttribute={editAttribute}
          deleteAttribute={deleteAttribute}
          index={index}
          readOnly={readOnly}
          showPrediction={showPrediction}
          isPublic={isPublic}
          hasError={hasError}
        />
      ))}
    </>
  );
}

const StatusMessage: { [key: string]: string } = {
  'No sales velocity': 'Generate Prediction',
};

interface IStatusRendererProps {
  item: IPOProduct;
  onClick?: (() => void) | undefined;
}

function StatusRenderer({ item, onClick }: IStatusRendererProps) {
  return (
    <>
      {item.predictedQty !== undefined && item.predictedQty ? (
        <div>
          <Button
            size="sm"
            className="sk:font-bold sk:text-success"
            onPress={() => onClick && onClick()}
          >
            {CommonUtility.numberWithCommas(item.predictedQty)}
          </Button>
        </div>
      ) : (
        <TextAction
          onClick={() => onClick && onClick()}
          className={cn(
            item.predictions?.statusMessageColor,
            'sk:mx-auto sk:flex sk:w-26 sk:items-center sk:justify-center sk:rounded-sm sk:font-bold sk:whitespace-pre-wrap',
            item.predictions?.statusMessageColor === 'green'
              ? 'sk:text-success'
              : 'sk:text-fg',
            StatusMessage[item.predictions?.statusMessage] &&
              'sk:border sk:border-solid sk:border-transparent sk:p-2 sk:hover:border-border',
          )}
        >
          {StatusMessage[item.predictions?.statusMessage] ||
            item.predictions?.statusMessage ||
            'Collecting more data...'}
        </TextAction>
      )}
      {item.purchaseOrdersInProgressDetails.length > 0 &&
        item.purchaseOrdersInProgressDetails[0] && (
          <MuteText>
            {item.purchaseOrdersInProgressDetails.length} Purchase Order In
            Progress
          </MuteText>
        )}
    </>
  );
}

interface IPredicateSelectAllProps {
  selectAllPredicateClick: () => void;
}

export function PredicateSelectAll({
  selectAllPredicateClick,
}: IPredicateSelectAllProps) {
  return (
    <div className="sk:flex sk:flex-col">
      <span>Prediction (Units)</span>
      <TextAction
        className="sk:mt-1 sk:font-bold sk:text-success sk:hover:underline"
        onClick={() => selectAllPredicateClick()}
      >
        Select All
      </TextAction>
    </div>
  );
}

export function PredicateDeSelectAll({
  selectAllPredicateClick,
}: IPredicateSelectAllProps) {
  return (
    <div className="sk:flex sk:flex-col">
      <span>Prediction (Units)</span>
      <TextAction
        className="sk:mt-1 sk:font-bold sk:text-danger sk:hover:underline"
        onClick={() => selectAllPredicateClick()}
      >
        Clear All
      </TextAction>
    </div>
  );
}
